// Enhanced image generation service with multiple API fallbacks and robust error handling
import { GoogleGenerativeAI } from '@google/generative-ai';

// Google API keys for Imagen
const GOOGLE_API_KEYS = [
	"AIzaSyDPT2duNIpH6-V43gGSs8X1JEK23oKPY48",
	"AIzaSyD4EY6klxOpj-AeLTvpVG-5euK1ZM4TzyY",
	"AIzaSyBEllrtfE8HdZbp8s1M2a7V_ok6teOnsP8"
].filter(key => key && key.trim() !== '');

console.log(`🔑 Google Imagen: ${GOOGLE_API_KEYS.length} API keys loaded`);

let currentKeyIndex = 0;

// Function to switch to the next API key
function switchToNextApiKey() {
	if (currentKeyIndex < GOOGLE_API_KEYS.length - 1) {
		currentKeyIndex++;
		console.log(`Switching to Google API key ${currentKeyIndex + 1}`);
		return true;
	}
	return false;
}

// Function to get current API key
function getCurrentApiKey() {
	return GOOGLE_API_KEYS[currentKeyIndex];
}

// Function to get image dimensions based on aspect ratio
function getImageDimensions(aspectRatio) {
	switch (aspectRatio) {
		case "1:1":
			return { width: 1024, height: 1024 };
		case "3:4":
			return { width: 768, height: 1024 };
		case "4:3":
			return { width: 1024, height: 768 };
		case "16:9":
			return { width: 1024, height: 576 };
		case "9:16":
			return { width: 576, height: 1024 };
		default:
			return { width: 1024, height: 1024 };
	}
}

// Google Imagen API generation with Vertex AI
async function generateWithGoogleImagen(prompt, aspectRatio) {
	if (GOOGLE_API_KEYS.length === 0) {
		throw new Error("No Google API keys available");
	}

	const dims = getImageDimensions(aspectRatio);
	let lastError = null;

	// Try each API key
	for (let attempt = 0; attempt < GOOGLE_API_KEYS.length; attempt++) {
		try {
			const currentKey = getCurrentApiKey();
			// console.log(`🎨 Trying Google Imagen with API key ${currentKeyIndex + 1}/${GOOGLE_API_KEYS.length}`);

			const genAI = new GoogleGenerativeAI(currentKey);
			const model = genAI.getGenerativeModel({ model: "imagen-3.0-generate-001" });

			const result = await model.generateContent([
				{
					text: prompt
				}
			]);

			if (result && result.response) {
				const imageData = result.response.candidates?.[0]?.content?.parts?.[0]?.inlineData;
				
				if (imageData && imageData.data) {
					const base64Data = imageData.data;
					const mimeType = imageData.mimeType || 'image/png';
					const dataUrl = `data:${mimeType};base64,${base64Data}`;

					// console.log("✅ Google Imagen generation successful!");
					return {
						url: dataUrl,
						base64: base64Data,
						dimensions: dims,
						prompt: prompt,
						model: "Google Imagen 3.0",
						aspectRatio: aspectRatio,
						downloadable: true
					};
				}
			}

			throw new Error("Invalid response structure from Google Imagen");

		} catch (error) {
			// console.error(`❌ Google Imagen attempt ${attempt + 1} failed:`, error);
			lastError = error;

			// If quota exceeded or API issue, try next key
			if (error.message?.includes('quota') || 
				error.message?.includes('billing') || 
				error.message?.includes('403') ||
				error.message?.includes('429')) {
				
				if (switchToNextApiKey()) {
					// console.log("🔄 Switching to next API key due to quota/billing issue");
					continue;
				}
			}

			// For other errors, still try next key
			if (attempt < GOOGLE_API_KEYS.length - 1) {
				switchToNextApiKey();
				continue;
			}
		}
	}

	// If all Google keys failed, throw the last error
	throw new Error(`Google Imagen failed after ${GOOGLE_API_KEYS.length} attempts: ${lastError?.message || 'Unknown error'}`);
}

// Enhanced Pollinations API with retry logic
async function generateWithPollinations(prompt, aspectRatio) {
	const dims = getImageDimensions(aspectRatio);
	const maxRetries = 3;
	
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			// console.log(`🌸 Generating with Pollinations AI (attempt ${attempt + 1}/${maxRetries})...`);
			
			// Enhanced prompt for better results
			const enhancedPrompt = `${prompt}, high quality, detailed, professional, 4k resolution`;
			const apiUrl = `https://image.pollinations.ai/prompt/${encodeURIComponent(enhancedPrompt)}?width=${dims.width}&height=${dims.height}&model=flux&enhance=true&nologo=true&seed=${Date.now()}`;
			
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

			const response = await fetch(apiUrl, {
				signal: controller.signal,
				headers: {
					'Accept': 'image/*',
					'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
				}
			});

			clearTimeout(timeoutId);

			if (!response.ok) {
				throw new Error(`Pollinations API error: ${response.status} ${response.statusText}`);
			}

			const blob = await response.blob();
			
			// Validate that we got an image
			if (!blob.type.startsWith('image/')) {
				throw new Error('Invalid response: not an image');
			}

			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onloadend = () => {
					const base64 = reader.result.split(',')[1];
					resolve({
						url: reader.result,
						base64: base64,
						dimensions: dims,
						prompt: prompt,
						model: "Pollinations AI (Enhanced)",
						aspectRatio: aspectRatio,
						downloadable: true
					});
				};
				reader.onerror = () => reject(new Error('Failed to process image data'));
				reader.readAsDataURL(blob);
			});

		} catch (error) {
			// console.error(`❌ Pollinations attempt ${attempt + 1} failed:`, error);
			
			if (attempt === maxRetries - 1) {
				throw error;
			}
			
			// Wait before retry
			await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
		}
	}
}

// Alternative free APIs for additional fallbacks
async function generateWithHuggingFace(prompt, aspectRatio) {
	try {
		// console.log("🤗 Trying Hugging Face API...");
		const dims = getImageDimensions(aspectRatio);
		
		const response = await fetch("https://api-inference.huggingface.co/models/stabilityai/stable-diffusion-xl-base-1.0", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				inputs: prompt,
				parameters: {
					width: dims.width,
					height: dims.height,
					num_inference_steps: 50,
					guidance_scale: 7.5
				}
			}),
		});

		if (!response.ok) {
			throw new Error(`Hugging Face API error: ${response.status}`);
		}

		const blob = await response.blob();
		
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				const base64 = reader.result.split(',')[1];
				resolve({
					url: reader.result,
					base64: base64,
					dimensions: dims,
					prompt: prompt,
					model: "Hugging Face SDXL",
					aspectRatio: aspectRatio,
					downloadable: true
				});
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});

	} catch (error) {
		// console.error("❌ Hugging Face API failed:", error);
		throw error;
	}
}

// DeepAI API fallback
async function generateWithDeepAI(prompt, aspectRatio) {
	try {
		// console.log("🔮 Trying DeepAI API...");
		const dims = getImageDimensions(aspectRatio);
		
		const formData = new FormData();
		formData.append('text', prompt);
		formData.append('width', dims.width.toString());
		formData.append('height', dims.height.toString());

		const response = await fetch('https://api.deepai.org/api/text2img', {
			method: 'POST',
			body: formData,
		});

		if (!response.ok) {
			throw new Error(`DeepAI API error: ${response.status}`);
		}

		const result = await response.json();
		
		if (result.output_url) {
			// Fetch the actual image
			const imageResponse = await fetch(result.output_url);
			const blob = await imageResponse.blob();
			
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onloadend = () => {
					const base64 = reader.result.split(',')[1];
					resolve({
						url: reader.result,
						base64: base64,
						dimensions: dims,
						prompt: prompt,
						model: "DeepAI",
						aspectRatio: aspectRatio,
						downloadable: true
					});
				};
				reader.onerror = reject;
				reader.readAsDataURL(blob);
			});
		}
		
		throw new Error("No image URL in DeepAI response");

	} catch (error) {
		// console.error("❌ DeepAI API failed:", error);
		throw error;
	}
}

// Enhanced creative placeholder generator with better visuals
function generateCreativePlaceholder(prompt, aspectRatio) {
	// console.log("🎨 Generating enhanced creative placeholder...");
	
	const canvas = document.createElement('canvas');
	const dims = getImageDimensions(aspectRatio);
	canvas.width = dims.width;
	canvas.height = dims.height;
	const ctx = canvas.getContext('2d');

	// Create sophisticated gradient background
	const gradient = ctx.createRadialGradient(
		canvas.width / 2, canvas.height / 2, 0,
		canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height) / 2
	);
	gradient.addColorStop(0, '#667eea');
	gradient.addColorStop(0.5, '#764ba2');
	gradient.addColorStop(1, '#2C1810');
	ctx.fillStyle = gradient;
	ctx.fillRect(0, 0, canvas.width, canvas.height);

	// Add decorative geometric elements
	ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
	for (let i = 0; i < 20; i++) {
		const x = Math.random() * canvas.width;
		const y = Math.random() * canvas.height;
		const size = Math.random() * 60 + 20;
		
		if (i % 3 === 0) {
			// Circles
			ctx.beginPath();
			ctx.arc(x, y, size / 2, 0, Math.PI * 2);
			ctx.fill();
		} else if (i % 3 === 1) {
			// Rectangles
			ctx.fillRect(x - size/2, y - size/2, size, size);
		} else {
			// Triangles
			ctx.beginPath();
			ctx.moveTo(x, y - size/2);
			ctx.lineTo(x - size/2, y + size/2);
			ctx.lineTo(x + size/2, y + size/2);
			ctx.closePath();
			ctx.fill();
		}
	}

	// Add central icon
	ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
	ctx.font = '72px Arial';
	ctx.textAlign = 'center';
	ctx.fillText('🎨', canvas.width / 2, canvas.height / 2 - 100);

	// Add prompt text with better formatting
	ctx.fillStyle = 'white';
	ctx.font = 'bold 32px Arial';
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	
	// Enhanced text wrapping
	const words = prompt.split(' ');
	const lines = [];
	let currentLine = '';
	const maxWidth = canvas.width - 120;
	
	words.forEach(word => {
		const testLine = currentLine + word + ' ';
		const metrics = ctx.measureText(testLine);
		if (metrics.width > maxWidth && currentLine !== '') {
			lines.push(currentLine.trim());
			currentLine = word + ' ';
		} else {
			currentLine = testLine;
		}
	});
	lines.push(currentLine.trim());

	const lineHeight = 40;
	const startY = canvas.height / 2 - ((lines.length - 1) * lineHeight) / 2;
	
	// Add text shadow
	ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
	ctx.shadowBlur = 4;
	ctx.shadowOffsetX = 2;
	ctx.shadowOffsetY = 2;
	
	lines.forEach((line, index) => {
		ctx.fillText(line, canvas.width / 2, startY + index * lineHeight);
	});

	// Reset shadow
	ctx.shadowColor = 'transparent';
	ctx.shadowBlur = 0;
	ctx.shadowOffsetX = 0;
	ctx.shadowOffsetY = 0;

	// Add status indicator
	ctx.font = '16px Arial';
	ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
	ctx.fillText('🤖 Creative AI Placeholder - Try refreshing or check your connection', canvas.width / 2, canvas.height - 40);

	// Add aspect ratio indicator
	ctx.font = '14px Arial';
	ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
	ctx.fillText(`${aspectRatio} • ${dims.width}×${dims.height}`, canvas.width / 2, canvas.height - 20);

	const base64 = canvas.toDataURL('image/png').split(',')[1];
	
	return {
		url: canvas.toDataURL('image/png'),
		base64: base64,
		dimensions: dims,
		prompt: prompt,
		model: "Creative Placeholder (Enhanced)",
		aspectRatio: aspectRatio,
		downloadable: true,
		isPlaceholder: true
	};
}

// Enhanced download function with better error handling
export function downloadImage(imageUrl, filename = null) {
	try {
		const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
		const defaultFilename = `ai-generated-image-${timestamp}.png`;
		const finalFilename = filename || defaultFilename;

		const link = document.createElement('a');
		link.href = imageUrl;
		link.download = finalFilename;
		link.style.display = 'none';
		
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		
		// console.log(`✅ Downloaded: ${finalFilename}`);
		return true;
	} catch (error) {
		console.error('❌ Download failed:', error);
		
		// Fallback: open in new tab
		try {
			window.open(imageUrl, '_blank');
			// console.log('📖 Opened image in new tab as fallback');
			return true;
		} catch (fallbackError) {
			console.error('❌ Fallback download also failed:', fallbackError);
			return false;
		}
	}
}

// Main function with comprehensive cascading fallbacks
export async function generateImageWithGoogleImagen(prompt, aspectRatio = "1:1") {
	// console.log("🎨 Starting enhanced AI image generation...");
	// console.log(`📝 Prompt: "${prompt}"`);
	// console.log(`📐 Aspect Ratio: ${aspectRatio}`);

	if (!prompt || prompt.trim().length === 0) {
		throw new Error("Prompt cannot be empty");
	}

	const cleanPrompt = prompt.trim();
	const apis = [
		{ name: "Google Imagen", func: generateWithGoogleImagen, priority: 1 },
		{ name: "ChatBox AI", func: generateWithPollinations, priority: 2 },
		{ name: "Hugging Face", func: generateWithHuggingFace, priority: 3 },
		{ name: "DeepAI", func: generateWithDeepAI, priority: 4 }
	];

	let lastError = null;
	let fallbackUsed = false;

	// Try each API in order of priority
	for (const api of apis) {
		try {
			// console.log(`🔄 Trying ${api.name}...`);
			const result = await api.func(cleanPrompt, aspectRatio);
			
			if (result && result.url) {
				// console.log(`✅ Image generated successfully with ${api.name}!`);
				result.generatedBy = api.name;
				result.timestamp = new Date().toISOString();
				return result;
			}
		} catch (error) {
			console.error(`❌ ${api.name} failed:`, error);
			lastError = error;
			
			// Add delay between attempts to avoid rate limiting
			if (api.priority < apis.length) {
				await new Promise(resolve => setTimeout(resolve, 1000));
			}
		}
	}

	// If all APIs failed, generate enhanced placeholder
	// console.log("⚠️ All APIs failed, generating enhanced creative placeholder...");
	fallbackUsed = true;
	
	try {
		const placeholder = generateCreativePlaceholder(cleanPrompt, aspectRatio);
		placeholder.generatedBy = "Enhanced Placeholder";
		placeholder.timestamp = new Date().toISOString();
		placeholder.error = lastError?.message || "All image generation APIs failed";
		
		// console.log("✅ Enhanced placeholder generated successfully!");
		return placeholder;
	} catch (placeholderError) {
		console.error("❌ Even placeholder generation failed:", placeholderError);
		throw new Error(`Complete failure: ${lastError?.message || 'Unknown error'} | Placeholder error: ${placeholderError.message}`);
	}
}

// Additional utility functions
export function validatePrompt(prompt) {
	if (!prompt || typeof prompt !== 'string') {
		return { valid: false, error: "Prompt must be a non-empty string" };
	}
	
	const cleanPrompt = prompt.trim();
	if (cleanPrompt.length === 0) {
		return { valid: false, error: "Prompt cannot be empty" };
	}
	
	if (cleanPrompt.length > 1000) {
		return { valid: false, error: "Prompt is too long (max 1000 characters)" };
	}
	
	return { valid: true, cleanPrompt };
}

export function getSupportedAspectRatios() {
	return [
		{ value: "1:1", label: "Square (1:1)", dimensions: "1024×1024" },
		{ value: "16:9", label: "Landscape (16:9)", dimensions: "1024×576" },
		{ value: "9:16", label: "Portrait (9:16)", dimensions: "576×1024" },
		{ value: "3:4", label: "Portrait (3:4)", dimensions: "768×1024" },
		{ value: "4:3", label: "Landscape (4:3)", dimensions: "1024×768" }
	];
}

export function getApiStatus() {
	return {
		googleImagen: {
			available: GOOGLE_API_KEYS.length > 0,
			keyCount: GOOGLE_API_KEYS.length,
			currentKeyIndex: currentKeyIndex + 1
		},
		fallbacks: [
			{ name: "ChatBox AI", available: true, free: true },
			{ name: "Hugging Face", available: true, free: true },
			{ name: "DeepAI", available: true, free: true },
			{ name: "Creative Placeholder", available: true, free: true }
		]
	};
}

export default generateImageWithGoogleImagen;
